import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { toast } from 'react-toastify';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { Close } from '@mui/icons-material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
// COMPONENT
import BorderSection from '../../../components/BorderSection';
// API
import { postPlottedMdsBreakdown, postPlottedMdsBreakdownExport } from '../../../api/crmDashboard';
// CONTEXT
import useCallPerformanceContext from '../context/useCallPerformanceContext';

export default function PlottedMdsModal() {
  const { filterForm, plottedMdsModal, setPlottedMdsModal } = useContext(useCallPerformanceContext);
  const [total, setTotal] = useState({ tagged_mds: 0, plotted_mds: 0 });
  const [plottedData, setPlottedData] = useState({ loading: false, data: [] });
  const [unplottedData, setUnplottedData] = useState({ loading: false, data: [] });

  const handleCancel = () => {
    setPlottedMdsModal(false);
  };

  const [sortModel, setSortModel] = useState([
    {
      field: 'id',
      sort: 'desc',
    },
  ]);

  const columns = [
    {
      field: 'rsm_name',
      headerName: 'RSM',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'manager_name',
      headerName: 'Manager',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'mpr_name',
      headerName: 'MPR',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'md_name',
      headerName: 'MD',
      width: 200,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    },
  ];

  const handleExport = async (name) => {
    try {
      toast.loading('Please wait ...');

      const request = {
        export_type: name,
        team: filterForm?.team?.value,
        rep_id: filterForm?.rep_id?.value,
        date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
        date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
      };

      const res = await postPlottedMdsBreakdownExport(request);

      const { code, result } = res.data.response;
      if (code === '200') {
        window.location.href = result;
        toast.success(`Successfully downloaded file: ${res?.data?.response?.file_name}`);
      }
      toast.dismiss();
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const handleBreakdownView = async () => {
    try {
      setPlottedData({ ...plottedData, loading: true });
      setUnplottedData({ ...unplottedData, loading: true });
      const request = {
        team: filterForm?.team?.value,
        rep_id: filterForm?.rep_id?.value,
        date_from: moment(filterForm?.date_from).format('YYYY-MM-DD'),
        date_to: moment(filterForm?.date_to).format('YYYY-MM-DD'),
      };

      const response = await postPlottedMdsBreakdown(request);
      const { code } = response.data;

      if (code === '200') {
        setTotal({
          ...total,
          tagged_mds: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
            response?.data?.tagged_mds,
          ),
          plotted_mds: Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
            response?.data?.plotted_mds,
          ),
        });

        setPlottedData({ ...plottedData, loading: false, data: response?.data?.plotted_data });
        setUnplottedData({
          ...unplottedData,
          loading: false,
          data: response?.data?.unplotted_data,
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    handleBreakdownView();
  }, []);
  return (
    <div>
      <Dialog open={plottedMdsModal} onClose={handleCancel} maxWidth='auto' fullWidth>
        <DialogTitle>
          Plotted MDs Breakdown
          <IconButton
            aria-label='close'
            onClick={handleCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} pl={3}>
              <Typography sx={{ fontSize: '20px', fontWeight: 'bold', color: '#3B71CA' }}>
                Tagged MDs: {total.tagged_mds}
                <span style={{ marginLeft: '50px' }}>Plotted MDs: {total.plotted_mds}</span>
              </Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }} />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} p={1}>
              <BorderSection title='Plotted MDs'>
                <Grid xs={12} sm={12} md={12} lg={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      variant='contained'
                      color='warning'
                      onClick={() => handleExport('plotted_mds')}
                      startIcon={<CloudDownloadIcon />}
                      sx={{
                        marginLeft: '10px',
                        paddingTop: '10px',
                      }}
                    >
                      Export
                    </Button>
                  </Box>
                </Grid>
                <Grid xs={12} sm={12} md={12} lg={12}>
                  <Box
                    className='table-container'
                    sx={{
                      height: `600px`,
                      '& .super-app-theme--header': {
                        fontWeight: 'bold',
                        backgroundColor: '#808080',
                        color: '#fff',
                      },
                    }}
                  >
                    <DataGridPro
                      loading={plottedData?.loading}
                      rows={plottedData?.data}
                      columns={columns}
                      sortModel={sortModel}
                      experimentalFeatures={{ rowPinning: true }}
                      initialState={{
                        ...plottedData?.data.initialState,
                        pagination: {
                          pageSize: 50,
                        },
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                      pagination
                      onSortModelChange={(model) => setSortModel(model)}
                    />
                  </Box>
                </Grid>
              </BorderSection>
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} p={1}>
              <BorderSection title='Unplotted MDs'>
                <Grid xs={12} sm={12} md={12} lg={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      variant='contained'
                      color='warning'
                      onClick={() => handleExport('unplotted_mds')}
                      startIcon={<CloudDownloadIcon />}
                      sx={{
                        marginLeft: '10px',
                        paddingTop: '10px',
                      }}
                    >
                      Export
                    </Button>
                  </Box>
                </Grid>
                <Grid xs={12} sm={12} md={12} lg={12}>
                  <Box
                    className='table-container'
                    sx={{
                      height: `600px`,
                      '& .super-app-theme--header': {
                        fontWeight: 'bold',
                        backgroundColor: '#808080',
                        color: '#fff',
                      },
                    }}
                  >
                    <DataGridPro
                      loading={unplottedData?.loading}
                      rows={unplottedData?.data}
                      columns={columns}
                      sortModel={sortModel}
                      experimentalFeatures={{ rowPinning: true }}
                      initialState={{
                        ...unplottedData?.data.initialState,
                        pagination: {
                          pageSize: 50,
                        },
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                      pagination
                      onSortModelChange={(model) => setSortModel(model)}
                    />
                  </Box>
                </Grid>
              </BorderSection>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' color='error' onClick={handleCancel}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
